import { useState } from "react";
import { ExternalLink, MapPin, Clock, Building2, GraduationCap, Presentation, ArrowRight, ChevronDown, X, Maximize2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useSite, usePublic } from "../../lib/site";
import { stripHtml } from "../../lib/i18n";
import { img } from "../../lib/api";
import { Img } from "./Common";

const SECTIONS = [
  { kind: "received", icon: GraduationCap, title: { tr: "Aldığım Eğitimler", en: "Education" } },
  { kind: "given", icon: Presentation, title: { tr: "Verdiğim Eğitimler", en: "Trainings I Give" } },
];

function Chip({ icon: Icon, children }) {
  if (!children) return null;
  return <span className="inline-flex items-center gap-1.5 text-xs text-s-muted"><Icon size={13} className="text-s-primary" />{children}</span>;
}

function Item({ it, t, lang }) {
  const to = `${lang === "en" ? "/en" : ""}/egitimlerim/${it.slug}`;
  const hasDetail = !!(t(it.body) || it.certificate);
  const hasActions = hasDetail || it.link;
  const descText = t(it.description) || stripHtml(t(it.body));
  return (
    <div data-testid={`training-item-${it.id}`}
      className="group relative overflow-hidden rounded-2xl border border-site-border bg-[linear-gradient(150deg,color-mix(in_srgb,var(--s-primary)_5%,var(--s-surface)),var(--s-surface)_55%)] p-5 sm:p-6 transition-all duration-300 hover:border-[color:color-mix(in_srgb,var(--s-primary)_45%,transparent)] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-20px_rgba(0,0,0,0.6)]">
      <span className="absolute left-0 top-6 bottom-6 w-[3px] rounded-full bg-gradient-to-b from-s-primary to-transparent opacity-70" aria-hidden />
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          {it.date && <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.2em] text-s-primary mb-1.5">{it.date}</span>}
          <h3 className="font-heading text-lg sm:text-xl font-bold leading-snug tracking-tight">
            {hasDetail ? <Link to={to} className="hover:text-s-primary transition-colors" data-testid={`training-detail-link-${it.id}`}>{t(it.title)}</Link> : t(it.title)}
          </h3>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
            <Chip icon={Building2}>{t(it.org)}</Chip>
            <Chip icon={MapPin}>{it.location}</Chip>
            <Chip icon={Clock}>{it.duration_text}</Chip>
          </div>
          {descText && <p className="mt-3 text-sm text-s-text/70 leading-relaxed line-clamp-3 whitespace-pre-line">{descText}</p>}
        </div>
        {t(it.level) && <span className="shrink-0 text-[11px] font-medium px-3 py-1 rounded-full border border-[color:color-mix(in_srgb,var(--s-primary)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--s-primary)_10%,transparent)] text-s-primary">{t(it.level)}</span>}
      </div>
      {hasActions && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4">
          {hasDetail && <Link to={to} className="inline-flex items-center gap-1.5 text-sm font-semibold text-s-primary group-hover:gap-2.5 transition-all" data-testid={`training-detail-cta-${it.id}`}>{lang === "tr" ? "Detayı gör" : "View details"}<ArrowRight size={15} /></Link>}
          {it.link && <a href={it.link} target="_blank" rel="noopener noreferrer" data-testid={`training-link-${it.id}`} className="inline-flex items-center gap-1.5 text-sm text-s-muted hover:text-s-primary transition-colors">{lang === "tr" ? "Bağlantı" : "Link"}<ExternalLink size={13} /></a>}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, items, t, lang, defaultOpen, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-site-border last:border-0" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 py-5 text-left group" data-testid={`${testid}-toggle`}>
        <span className="flex items-center gap-2.5 font-heading text-lg sm:text-xl font-semibold">
          <Icon size={18} className="text-s-primary shrink-0" />{title}
          <span className="text-s-muted font-normal text-sm">({items.length})</span>
        </span>
        <ChevronDown size={18} className={`text-s-muted transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-6 space-y-3">{items.map((it) => <Item key={it.id} it={it} t={t} lang={lang} />)}</div>}
    </div>
  );
}

function Lightbox({ item, t, onClose }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-testid="cert-lightbox">
      <button onClick={onClose} aria-label="close" className="absolute top-5 right-5 text-white/90 hover:text-white p-2" data-testid="cert-lightbox-close"><X size={28} /></button>
      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img src={img(item.certificate)} alt={t(item.title)} loading="lazy" decoding="async" className="w-full max-h-[82vh] object-contain rounded-lg shadow-2xl" />
        <p className="text-white/90 text-center mt-4 text-sm">{t(item.title)}{item.date ? ` · ${item.date}` : ""}</p>
      </div>
    </div>
  );
}

function CertGrid({ items, t, lang, onOpen }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="certificates-gallery">
      {items.map((it) => (
        <button key={it.id} type="button" onClick={() => onOpen(it)} data-testid={`cert-${it.id}`}
          className="group relative block rounded-xl overflow-hidden border border-site-border text-left">
          <Img src={it.certificate} alt={t(it.title)} className="w-full aspect-[4/3] object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
          <span className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Maximize2 size={15} /></span>
          <span className="absolute bottom-0 inset-x-0 p-3">
            {it.date && <span className="block text-[10px] font-semibold uppercase tracking-widest text-s-primary mb-0.5">{it.date}</span>}
            <span className="block text-sm font-semibold text-white leading-snug line-clamp-2">{t(it.title)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

export function TrainingsTimeline({ page }) {
  const { t, lang } = useSite();
  const { data = [] } = usePublic(["trainings"], "/public/trainings");
  const [tab, setTab] = useState("egitimler");
  const [lightbox, setLightbox] = useState(null);
  if (!data.length) return null;

  const visible = { received: page?.trainings_show_received !== false, given: page?.trainings_show_given !== false };
  const sections = SECTIONS.filter((s) => visible[s.kind] && data.some((d) => (d.kind || "received") === s.kind));
  const certs = data.filter((d) => d.certificate);
  const tabs = [["egitimler", lang === "tr" ? "Eğitimler" : "Trainings"]];
  if (certs.length) tabs.push(["sertifikalar", lang === "tr" ? "Sertifikalar" : "Certificates"]);
  const activeTab = tab === "sertifikalar" && !certs.length ? "egitimler" : tab;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 mt-8" data-testid="trainings-timeline">
      {tabs.length > 1 && (
        <div className="flex gap-7 border-b border-site-border mb-6" data-testid="trainings-tabs">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`trainings-tab-${k}`}
              className={`pb-3 -mb-px text-sm font-medium border-b-2 transition-colors ${activeTab === k ? "border-s-primary text-s-text" : "border-transparent text-s-muted hover:text-s-text"}`}>{label}</button>
          ))}
        </div>
      )}

      {activeTab === "egitimler" && (
        <div data-testid="trainings-tab-panel">
          {sections.length === 0
            ? <p className="text-s-muted text-sm py-6" data-testid="trainings-empty">{lang === "tr" ? "Henüz eğitim eklenmedi." : "No trainings yet."}</p>
            : sections.map(({ kind, icon, title }, i) => (
                <Section key={kind} title={t(title)} icon={icon} defaultOpen={i === 0}
                  items={data.filter((d) => (d.kind || "received") === kind)} t={t} lang={lang} testid={`trainings-section-${kind}`} />
              ))}
        </div>
      )}

      {activeTab === "sertifikalar" && certs.length > 0 && (
        <div data-testid="certificates-tab-panel"><CertGrid items={certs} t={t} lang={lang} onOpen={setLightbox} /></div>
      )}

      {lightbox && <Lightbox item={lightbox} t={t} onClose={() => setLightbox(null)} />}
    </div>
  );
}
